import { useEffect, useRef, useState } from "react";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { formatMoney } from "../format";
import { parseCredits } from "../payout";
import { api, newIdempotencyKey, type Withdrawals } from "./api";
import EarningsPanel from "./EarningsPanel";

const inputClass =
  "h-11 w-full rounded-md border border-ground-edge bg-ground px-3.5 text-sm text-chrome placeholder:text-chrome-dim transition-colors focus:border-gold/60 focus:outline-none";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function WithdrawalsTab({ paypalEmail }: { paypalEmail: string }) {
  const t = copy.dashboard.withdrawals;
  const [data, setData] = useState<Withdrawals | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState(paypalEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // One key per withdrawal attempt: a retry after a failure or a lost
  // response resends it, so the server debits once. A new key only after a
  // confirmed success.
  const key = useRef(newIdempotencyKey());

  const load = async () => {
    const res = await api.withdrawals();
    if (res.ok) {
      setData(res.data);
      setLoadError(false);
    } else setLoadError(true);
  };

  useEffect(() => {
    void load();
  }, []);

  if (loadError && !data) {
    return (
      <div className="rounded-2xl border border-ground-edge bg-ground-raised p-8 text-center">
        <p className="text-sm text-chrome-dim">{copy.dashboard.error}</p>
        <button type="button" onClick={load} className="bf-focusable mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-money">
          {copy.dashboard.retry}
        </button>
      </div>
    );
  }
  if (!data) return null;

  const cents = parseCredits(amount);
  const reason =
    amount.trim() === ""
      ? ""
      : cents === null
        ? t.enterAmount
        : cents < data.minimum_cents
          ? t.belowMin(data.minimum_display)
          : cents > data.available_cents
            ? t.overAvailable(data.available_display)
            : !EMAIL.test(email.trim())
              ? t.badEmail
              : "";
  const canSubmit = cents !== null && reason === "" && !busy;

  const submit = async () => {
    if (cents === null || !canSubmit) return;
    setBusy(true);
    setError("");
    setNotice("");
    const res = await api.requestWithdrawal(cents, email.trim(), key.current);
    setBusy(false);
    if (res.ok) {
      key.current = newIdempotencyKey();
      setAmount("");
      setNotice(t.requested(res.data.amount_display));
      await load();
      return;
    }
    setError(res.error);
  };

  return (
    <div className="flex flex-col gap-5">
      <EarningsPanel />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-chrome-dim">{t.available}</p>
          <p data-testid="available" className="bf-tabular mt-2 font-headline text-4xl leading-none text-money">
            {data.available_display}
          </p>
          <p className="mt-2 text-[11px] leading-snug text-chrome-dim">{t.availableSub}</p>
        </div>
        <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-chrome-dim">{t.pending}</p>
          <p data-testid="pending" className="bf-tabular mt-2 font-headline text-4xl leading-none text-gold">
            {data.pending_display}
          </p>
          <p className="mt-2 text-[11px] leading-snug text-chrome-dim">{t.pendingSub}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-7">
        <h3 className="font-headline text-2xl uppercase leading-none text-chrome">{t.heading}</h3>
        <p className="mt-2 text-sm text-chrome-dim">{t.sub(data.minimum_display)}</p>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div>
            <label htmlFor="wd-amount" className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
              {t.amount}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-chrome-dim">$</span>
              <input
                id="wd-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError("");
                  setNotice("");
                }}
                placeholder="0.00"
                className={`${inputClass} pl-7`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="wd-email" className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
              {t.paypal}
            </label>
            <div className="relative">
              <Icon name="paypal" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-chrome-dim" />
              <input
                id="wd-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="you@email.com"
                className={`${inputClass} pl-10`}
              />
            </div>
          </div>
        </div>

        {cents !== null && reason === "" && (
          <p data-testid="withdraw-receive" className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-money">
            {t.receive(formatMoney(cents))}
          </p>
        )}
        {reason && (
          <p data-testid="withdraw-reason" className="mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gold">
            <Icon name="errorWarning" className="text-base" />
            {reason}
          </p>
        )}
        {error && (
          <p role="alert" className="bf-fade-in mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-alert">
            <Icon name="errorWarning" className="text-base" />
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="bf-fade-in mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-money">
            <Icon name="checkboxCircle" className="text-base" />
            {notice}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="bf-focusable mt-6 h-11 w-full cursor-pointer whitespace-nowrap rounded-md bg-money text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8"
        >
          {busy ? t.submitting : t.submit}
        </button>
        <p className="mt-4 max-w-2xl text-xs leading-relaxed text-chrome-dim">{t.confirm}</p>
      </div>

      <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-7">
        <h3 className="font-headline text-2xl uppercase leading-none text-chrome">{t.history}</h3>
        {data.items.length === 0 ? (
          <p data-testid="withdrawals-empty" className="mt-6 text-sm text-chrome-dim">
            {t.empty}
          </p>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-left">
              <thead>
                <tr className="border-b border-ground-edge">
                  {[t.columns.amount, t.columns.destination, t.columns.requested, t.columns.status].map((head) => (
                    <th key={head} className="pb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} data-testid="withdrawal-row" className="border-b border-ground-edge/60">
                    <td className="bf-tabular py-3.5 font-headline text-lg text-chrome">{item.amount_display}</td>
                    <td className="py-3.5 text-xs text-chrome-dim">{item.destination}</td>
                    <td className="py-3.5 text-xs text-chrome-dim">{when(item.requested_at)}</td>
                    <td className="py-3.5">
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                          item.state === "pending" ? "border-gold/40 bg-gold/10 text-gold" : "border-money/40 bg-money/10 text-money"
                        }`}
                      >
                        {t.states[item.state] ?? item.state}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
